import { useEffect, useRef, useState } from "react";
import { useAsync } from "./useAsync";

export const COLLECTION_PAGE_SIZE = 25;
export function useMobileCollectionLayout() {
  const [mobile, setMobile] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 900px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 900px)");
    const update = () => setMobile(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return mobile;
}

export function mergeCollectionPage<T extends { id: string }>(previous: T[], next: T[]): T[] {
  const updates = new Map(next.map(item => [item.id, item]));
  const seen = new Set(previous.map(item => item.id));
  return [...previous.map(item => updates.get(item.id) ?? item), ...next.filter(item => !seen.has(item.id))];
}

// A response carries its own page/filter/revision. Never append a response from
// the previous page while React is changing URL state or recovering from sleep.
export function usePagedCollection<T extends { id: string }>(
  scope: string, page: number, changePage: (page: number) => void,
  load: (page: number, signal: AbortSignal) => Promise<{ items: T[]; total: number }>,
) {
  const mobile = useMobileCollectionLayout();
  const [revision, setRevision] = useState(0);
  const key = scope + ":" + revision;
  const [accumulated, setAccumulated] = useState<{ key: string; items: T[] }>({ key: "", items: [] });
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLElement>(null);
  const result = useAsync(async signal => {
    const data = await load(page, signal);
    return { ...data, page, key };
  }, [key, page], { keepPreviousData: true });
  const matching = result.data?.key === key;
  const ready = matching && result.data?.page === page;
  const busy = !result.error && (result.loading || result.refreshing || !ready);
  useEffect(() => {
    if (!ready || !result.data) return;
    const data = result.data;
    setAccumulated(current => ({
      key, items: !mobile || page === 1 || current.key !== key
        ? data.items : mergeCollectionPage(current.items, data.items),
    }));
  }, [result.data, ready, mobile, page, key]);
  // Mobile cannot start with a deep desktop page and omit all earlier entries.
  useEffect(() => { if (mobile) changePage(1); }, [mobile]);
  const items = mobile
    ? (accumulated.key === key ? accumulated.items : ready ? result.data!.items : [])
    : matching ? result.data!.items : [];
  const total = matching ? result.data!.total : 0;
  const totalPages = Math.max(1, Math.ceil(total / COLLECTION_PAGE_SIZE));
  useEffect(() => {
    if (!mobile || !loadMoreRef.current || busy || result.error || page >= totalPages) return;
    let advanced = false;
    const observer = new IntersectionObserver(([entry]) => {
      if (!advanced && entry.isIntersecting) { advanced = true; changePage(page + 1); }
    }, { rootMargin: "200px" });
    observer.observe(loadMoreRef.current);
    return () => { advanced = true; observer.disconnect(); };
  }, [mobile, busy, result.error, page, totalPages, key]);
  function showNewest() {
    topRef.current?.scrollIntoView({ block: "start" });
    setAccumulated({ key: "", items: [] });
    changePage(1);
    // Even when already on page 1, force a new request and cancel stale work.
    setRevision(value => value + 1);
  }
  return { items, total, totalPages, mobile, loadMoreRef, topRef, showNewest,
    loading: busy && !items.length, refreshing: busy, error: result.error,
    refresh: result.refresh, page, changePage };
}
